// Agreement Rate Selectors

import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import AutomatedScoringType from "enums/AutomatedScoringType";
import FeatureToggle from "enums/FeatureToggle";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import {
    selectIsAutomatedEvaluationForm,
    selectForm,
    selectQuestion,
} from "./qualityFormSlices";
import { AgreementRateData } from "./qualityFormSlices/qualityFormStates";

/**
 * Selects the form-level agreement rate data
 */
export const selectFormAgreementRate = (
    state: RootState
): AgreementRateData | undefined => {
    return state.qualityForm.data.form?.agreementRate?.formAgreementRate;
};

/**
 * Selects the loading state for form-level agreement rate
 */
export const selectFormAgreementRateLoading = (state: RootState): boolean => {
    return state.qualityForm.data.form?.agreementRate?.loading ?? false;
};

/**
 * Selects whether the form has agreement rate data available
 */
export const selectHasFormAgreementRateData = (state: RootState): boolean => {
    const agreementRateData = selectFormAgreementRate(state);
    return agreementRateData?.hasData ?? false;
};

/**
 * Selects the error state for form-level agreement rate
 */
export const selectFormAgreementRateError = (
    state: RootState
): string | undefined => {
    return state.qualityForm.data.form?.agreementRate?.error;
};

/**
 * Selects whether we should attempt to load agreement rate data
 * The thunk will determine if the current form is the latest published version
 */
export const selectShouldLoadAgreementRate = (state: RootState): boolean => {
    const isAgreementRateFeatureEnabled = selectFeatureToggle(
        state,
        FeatureToggle.AUTOCOMPLETE_EVALUATION_AGREEMENT_RATES
    );
    const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm(state);
    const isLoading = selectFormAgreementRateLoading(state);
    const hasData = selectHasFormAgreementRateData(state);
    const hasError = selectFormAgreementRateError(state) !== undefined;
    const currentForm = selectForm(state);

    const isPublished = !!currentForm?.published;

    // Only load if feature is enabled, form is automated, form is published, and we don't already have data/error and aren't currently loading
    const shouldLoad =
        isAgreementRateFeatureEnabled &&
        isAutomatedEvaluationForm &&
        isPublished &&
        !isLoading &&
        !hasData &&
        !hasError;

    return shouldLoad;
};

/**
 * Selects whether the form should display agreement rate
 */
export const selectShouldDisplayFormAgreementRate = (
    state: RootState
): boolean => {
    const isAgreementRateFeatureEnabled = selectFeatureToggle(
        state,
        FeatureToggle.AUTOCOMPLETE_EVALUATION_AGREEMENT_RATES
    );
    const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm(state);
    const hasData = selectHasFormAgreementRateData(state);
    const agreementRateData = selectFormAgreementRate(state);
    const currentForm = selectForm(state);
    const isPublished = !!currentForm?.published;

    return (
        isAgreementRateFeatureEnabled &&
        isAutomatedEvaluationForm &&
        hasData &&
        !!agreementRateData &&
        isPublished
    );
};

/**
 * Combined selector for agreement rate display state
 */
export const selectFormAgreementRateDisplayState = createSelector(
    [
        selectFormAgreementRate,
        selectFormAgreementRateLoading,
        selectFormAgreementRateError,
        selectShouldDisplayFormAgreementRate,
        selectHasFormAgreementRateData,
    ],
    (agreementRateData, loading, error, shouldDisplay, hasData) => ({
        data: agreementRateData,
        loading,
        error,
        shouldDisplay,
        hasData,
    })
);

/**
 * Selects the agreement rate data for a specific question by question ID
 */
export const selectQuestionAgreementRate = (
    state: RootState,
    questionId: string
): AgreementRateData | undefined => {
    return selectQuestion(state, questionId)?.agreementRate;
};

/**
 * Selects whether a question has agreement rate data available
 */
export const selectHasQuestionAgreementRateData = (
    state: RootState,
    questionId: string
): boolean => {
    const agreementRateData = selectQuestionAgreementRate(state, questionId);
    return agreementRateData?.hasData ?? false;
};

/**
 * Selects whether we should display agreement rate for a specific question
 */
export const selectShouldDisplayQuestionAgreementRate = (
    state: RootState,
    questionId: string
): boolean => {
    const isAgreementRateFeatureEnabled = selectFeatureToggle(
        state,
        FeatureToggle.AUTOCOMPLETE_EVALUATION_AGREEMENT_RATES
    );
    const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm(state);
    const question = state.qualityForm.data.questions?.[questionId];

    if (!question) {
        return false;
    }

    const hasAiScoringOrEa =
        question.automatedScoringType !== AutomatedScoringType.NO_AUTOMATION;

    return (
        isAgreementRateFeatureEnabled &&
        isAutomatedEvaluationForm &&
        hasAiScoringOrEa
    );
};

/**
 * Combined selector for question agreement rate display state
 */
export const selectQuestionAgreementRateDisplayState = createSelector(
    [
        (state: RootState, questionId: string) =>
            selectQuestionAgreementRate(state, questionId),
        (state: RootState, questionId: string) =>
            selectShouldDisplayQuestionAgreementRate(state, questionId),
        (state: RootState, questionId: string) =>
            selectHasQuestionAgreementRateData(state, questionId),
    ],
    (agreementRateData, shouldDisplay, hasData) => ({
        data: agreementRateData,
        shouldDisplay,
        hasData,
    })
);
